import { useState } from 'react'
import { Bug, CheckCircle2, AlertCircle, Plug, Unplug, X } from 'lucide-react'
import Spinner from '../Spinner'
import { trackSegmentEvent } from '../../lib/segment'
import type { Project, IssueTrackerProvider, IssueTrackerSettings } from '../../data/projectTypes'
import { useTestIssueTrackerMutation, useUpdateProjectMutation } from '../../services/projectDataApi'

// The issue-tracker config panel rendered INLINE in the running-project chat, surfaced when the QA
// agent calls the manage_issue_tracker tool. It lets the owner point QA at GitHub Issues, GitLab
// Issues, Linear, or Jira so every discovered bug (with its root cause, screenshots, and recordings)
// is filed as an issue there. Compact styling reuses chat.css .rp-card*, matching ProjectGithubChatCard.

const PROVIDERS: { id: IssueTrackerProvider; label: string }[] = [
  { id: 'github', label: 'GitHub Issues' },
  { id: 'gitlab', label: 'GitLab Issues' },
  { id: 'linear', label: 'Linear' },
  { id: 'jira', label: 'Jira' },
]

// The token label + help text per provider, so the single API-token field explains exactly what to paste.
const TOKEN_HELP: Record<IssueTrackerProvider, { label: string; hint: string }> = {
  github: {
    label: 'GitHub access token',
    hint: 'A personal access token with "repo" (or Issues: read & write) scope on the repository.',
  },
  gitlab: {
    label: 'GitLab access token',
    hint: 'A personal (or project) access token with the "api" scope and at least Reporter access to the project.',
  },
  linear: {
    label: 'Linear API key',
    hint: 'A personal API key from Linear → Settings → Security & access → Personal API keys.',
  },
  jira: {
    label: 'Jira API token',
    hint: 'An API token from id.atlassian.com → Security → API tokens, paired with your account email.',
  },
}

interface Draft {
  provider: IssueTrackerProvider
  githubRepoUrl: string
  gitlabProjectUrl: string
  linearTeamId: string
  jiraBaseUrl: string
  jiraProjectKey: string
  jiraEmail: string
  jiraIssueType: string
  apiToken: string
}

function draftFrom(settings: IssueTrackerSettings | null, preselect?: IssueTrackerProvider): Draft {
  return {
    provider: preselect ?? settings?.provider ?? 'github',
    githubRepoUrl: settings?.githubRepoUrl ?? '',
    gitlabProjectUrl: settings?.gitlabProjectUrl ?? '',
    linearTeamId: settings?.linearTeamId ?? '',
    jiraBaseUrl: settings?.jiraBaseUrl ?? '',
    jiraProjectKey: settings?.jiraProjectKey ?? '',
    jiraEmail: settings?.jiraEmail ?? '',
    jiraIssueType: settings?.jiraIssueType ?? 'Bug',
    apiToken: '',
  }
}

export default function IssueTrackerChatCard({
  project,
  provider,
  onClose,
  onResult,
}: {
  project: Project
  /** Provider the agent asked to preselect (the user named it). */
  provider?: IssueTrackerProvider
  onClose: () => void
  /** Append a short confirmation line to the transcript after a successful save/disconnect. */
  onResult?: (text: string) => void
}) {
  const [updateProject] = useUpdateProjectMutation()
  const [testIssueTracker] = useTestIssueTrackerMutation()
  const existing = project.issue_tracker
  const [draft, setDraft] = useState<Draft>(() => draftFrom(existing, provider))
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [test, setTest] = useState<{ ok: boolean; detail: string } | null>(null)

  // The stored config already has a token when apiTokenSet and the provider is unchanged — so an
  // empty token field means "keep the saved one" rather than "clear it".
  const hasStoredToken = !!existing?.apiTokenSet && existing.provider === draft.provider
  const tokenHelp = TOKEN_HELP[draft.provider]

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }))
    setError(null)
    setTest(null)
  }

  // Assemble the issue_tracker payload from the draft. Omit apiToken when the field is left blank and
  // a token is already stored (so saving other fields doesn't wipe the credential).
  function buildPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = { provider: draft.provider, enabled: true }
    if (draft.provider === 'github') payload.githubRepoUrl = draft.githubRepoUrl.trim()
    if (draft.provider === 'gitlab') payload.gitlabProjectUrl = draft.gitlabProjectUrl.trim()
    if (draft.provider === 'linear') payload.linearTeamId = draft.linearTeamId.trim()
    if (draft.provider === 'jira') {
      payload.jiraBaseUrl = draft.jiraBaseUrl.trim()
      payload.jiraProjectKey = draft.jiraProjectKey.trim()
      payload.jiraEmail = draft.jiraEmail.trim()
      payload.jiraIssueType = draft.jiraIssueType.trim() || 'Bug'
    }
    if (draft.apiToken.trim() || !hasStoredToken) payload.apiToken = draft.apiToken.trim()
    return payload
  }

  // Client-side check so we don't save an obviously-incomplete config (matches issueTrackerConfigProblem).
  function localProblem(): string | null {
    if (!draft.apiToken.trim() && !hasStoredToken) return `Enter ${tokenHelp.label.toLowerCase()}.`
    if (draft.provider === 'github' && !draft.githubRepoUrl.trim()) return 'Enter the GitHub repository URL.'
    if (draft.provider === 'gitlab' && !draft.gitlabProjectUrl.trim()) return 'Enter the GitLab project URL.'
    if (draft.provider === 'linear' && !draft.linearTeamId.trim()) return 'Enter the Linear team id.'
    if (draft.provider === 'jira') {
      if (!draft.jiraBaseUrl.trim()) return 'Enter the Jira site URL.'
      if (!draft.jiraProjectKey.trim()) return 'Enter the Jira project key.'
      if (!draft.jiraEmail.trim()) return 'Enter the Jira account email.'
    }
    return null
  }

  async function save(): Promise<boolean> {
    const problem = localProblem()
    if (problem) { setError(problem); return false }
    setSaving(true)
    setError(null)
    try {
      await updateProject({ id: project.id, data: { issue_tracker: buildPayload() } }).unwrap()
      const label = PROVIDERS.find((p) => p.id === draft.provider)?.label ?? draft.provider
      trackSegmentEvent('Connect issue tracker', { issueTracker: label })
      return true
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to save the issue tracker settings')
      return false
    } finally {
      setSaving(false)
    }
  }

  // Save first (so the server has the credential), then hit test-issue-tracker to validate it against
  // the provider's API without creating a real issue.
  async function saveAndTest() {
    setTest(null)
    if (!(await save())) return
    setTesting(true)
    try {
      const data = await testIssueTracker(project.id).unwrap()
      setTest({ ok: !!data.success, detail: data.detail || data.error || (data.success ? 'Connected.' : 'Connection failed.') })
    } catch {
      setTest({ ok: false, detail: 'Could not reach the test endpoint. Please try again.' })
    } finally {
      setTesting(false)
    }
  }

  async function saveAndClose() {
    if (!(await save())) return
    const label = PROVIDERS.find((p) => p.id === draft.provider)?.label ?? draft.provider
    onResult?.(`✓ QA will now file new bugs to ${label}. Each issue includes the bug's root cause, reproduction steps, screenshots, and Replay recording.`)
    onClose()
  }

  async function disconnect() {
    setDisconnecting(true)
    setError(null)
    try {
      await updateProject({ id: project.id, data: { issue_tracker: null } }).unwrap()
      onResult?.('✓ Disconnected the issue tracker. QA will stop filing bugs to it (they’re still tracked in Replay QA).')
      onClose()
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to disconnect the issue tracker')
      setDisconnecting(false)
    }
  }

  const busy = saving || testing || disconnecting

  return (
    <div className="rp-card" data-testid="issue-tracker-card" data-provider={draft.provider}>
      <div className="rp-card__head">
        <Bug size={15} className="rp-card__icon" aria-hidden />
        <span className="rp-card__title">File bugs to an issue tracker</span>
        <button type="button" className="rp-card__dismiss" onClick={onClose} aria-label="Close issue tracker panel" data-testid="issue-tracker-close">
          <X size={14} aria-hidden />
        </button>
      </div>
      <p className="rp-card__text">
        Pick where QA should file the bugs it finds. Each issue includes the full report — root cause,
        steps, screenshots, and the Replay recording.
      </p>

      <div className="rp-card__field">
        <label className="it-card__label" htmlFor="it-provider">Provider</label>
        <select
          id="it-provider"
          className="rp-card__input"
          value={draft.provider}
          onChange={(e) => { set('provider', e.target.value as IssueTrackerProvider) }}
          data-testid="issue-tracker-provider"
        >
          {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      {draft.provider === 'github' && (
        <div className="rp-card__field">
          <label className="it-card__label" htmlFor="it-gh-repo">Repository URL</label>
          <input id="it-gh-repo" type="url" className="rp-card__input" value={draft.githubRepoUrl}
            onChange={(e) => set('githubRepoUrl', e.target.value)} placeholder="https://github.com/owner/repo"
            autoComplete="off" data-testid="issue-tracker-github-repo" />
        </div>
      )}

      {draft.provider === 'gitlab' && (
        <div className="rp-card__field">
          <label className="it-card__label" htmlFor="it-gl-project">Project URL</label>
          <input id="it-gl-project" type="url" className="rp-card__input" value={draft.gitlabProjectUrl}
            onChange={(e) => set('gitlabProjectUrl', e.target.value)} placeholder="https://gitlab.com/group/project"
            autoComplete="off" data-testid="issue-tracker-gitlab-project" />
        </div>
      )}

      {draft.provider === 'linear' && (
        <div className="rp-card__field">
          <label className="it-card__label" htmlFor="it-linear-team">Team id</label>
          <input id="it-linear-team" type="text" className="rp-card__input" value={draft.linearTeamId}
            onChange={(e) => set('linearTeamId', e.target.value)} placeholder="9cfb482a-81e3-4154-b5b9-2c805e70a02d"
            autoComplete="off" data-testid="issue-tracker-linear-team" />
        </div>
      )}

      {draft.provider === 'jira' && (
        <>
          <div className="rp-card__field">
            <label className="it-card__label" htmlFor="it-jira-url">Site URL</label>
            <input id="it-jira-url" type="url" className="rp-card__input" value={draft.jiraBaseUrl}
              onChange={(e) => set('jiraBaseUrl', e.target.value)} placeholder="https://your-org.atlassian.net"
              autoComplete="off" data-testid="issue-tracker-jira-url" />
          </div>
          <div className="rp-card__field">
            <label className="it-card__label" htmlFor="it-jira-key">Project key</label>
            <input id="it-jira-key" type="text" className="rp-card__input" value={draft.jiraProjectKey}
              onChange={(e) => set('jiraProjectKey', e.target.value)} placeholder="QA"
              autoComplete="off" data-testid="issue-tracker-jira-key" />
          </div>
          <div className="rp-card__field">
            <label className="it-card__label" htmlFor="it-jira-email">Account email</label>
            <input id="it-jira-email" type="email" className="rp-card__input" value={draft.jiraEmail}
              onChange={(e) => set('jiraEmail', e.target.value)} placeholder="you@your-org.com"
              autoComplete="off" data-testid="issue-tracker-jira-email" />
          </div>
          <div className="rp-card__field">
            <label className="it-card__label" htmlFor="it-jira-type">Issue type</label>
            <input id="it-jira-type" type="text" className="rp-card__input" value={draft.jiraIssueType}
              onChange={(e) => set('jiraIssueType', e.target.value)} placeholder="Bug"
              autoComplete="off" data-testid="issue-tracker-jira-type" />
          </div>
        </>
      )}

      <div className="rp-card__field">
        <label className="it-card__label" htmlFor="it-token">{tokenHelp.label}</label>
        <input id="it-token" type="password" className="rp-card__input" value={draft.apiToken}
          onChange={(e) => set('apiToken', e.target.value)}
          placeholder={hasStoredToken ? '•••••••• (stored — leave blank to keep)' : ''}
          autoComplete="off" data-testid="issue-tracker-token" />
        <p className="rp-card__text it-card__hint">{tokenHelp.hint}</p>
      </div>

      {test && (
        <p className="rp-card__text" data-testid="issue-tracker-test-result">
          {test.ok
            ? <><CheckCircle2 size={13} className="rp-card__icon rp-card__icon--ok" aria-hidden /> {test.detail}</>
            : <><AlertCircle size={13} className="rp-card__icon" aria-hidden /> {test.detail}</>}
        </p>
      )}
      {error && <p className="rp-card__text" data-testid="issue-tracker-error">{error}</p>}

      <div className="rp-card__actions">
        <button type="button" className="rp-card__copy" onClick={saveAndClose} disabled={busy} data-testid="issue-tracker-save" data-analytics-event="chat-issue-tracker-save">
          {saving ? <><Spinner size={13} className="rp-card__icon" /> Saving…</> : <><Plug size={13} aria-hidden /> Save</>}
        </button>
        <button type="button" className="rp-card__copy" onClick={saveAndTest} disabled={busy} data-testid="issue-tracker-test" data-analytics-event="chat-issue-tracker-test">
          {testing ? <><Spinner size={13} className="rp-card__icon" /> Testing…</> : 'Save & test connection'}
        </button>
        {existing && (
          <button type="button" className="rp-card__copy" onClick={disconnect} disabled={busy} data-testid="issue-tracker-disconnect" data-analytics-event="chat-issue-tracker-disconnect">
            <Unplug size={13} aria-hidden /> {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </button>
        )}
      </div>
    </div>
  )
}
